import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./intake.css";
import { trackQuestionAnswered, trackQuestionEdited, trackFormCompleted } from "../analytics/gtag";
import { callBackendApiPost } from "../lib/api";
import { buildApiPayload } from "../lib/payload";
import { getAnswerById } from "../lib/helpers";
import { getVisibleQuestions } from "../lib/visibility";
import { formatAnswer } from "../lib/formatAnswer";
import { renderQuestionComponent } from "../lib/questionRenderer";
import logDrtEvent from "../lib/logging";

import { questions } from "../config/questions";

import useAutoScroll from "../hooks/useAutoScroll";
import useIntakeAnalytics from "../hooks/useIntakeAnalytics";
import SummaryScreen from "./components/SummaryScreen";
import ThankYouScreen from "./components/ThankYouScreen";

function Intake() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [formStartTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [infantBlocked, setInfantBlocked] = useState(false);

  const [showSummary, setShowSummary] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [welcomeMessageIndex, setWelcomeMessageIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const currentQuestionRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const inputEndRef = useRef(null);

  const isUpdatingRef = useRef(false);
  const hasPostedProgressRef = useRef(false);

  // Visible questions from helpers
  const visibleQuestions = getVisibleQuestions(questions, answers);
  const isComplete = currentQuestionIndex >= visibleQuestions.length;

  // Show ALL non-hidden (non-system, non-credit-card) questions in Summary
  const summaryQuestions = questions.filter(
    (q) => !q.hidden && q.type !== "system" && q.type !== "credit-card"
  );

  // Analytics hooks
  useIntakeAnalytics({
    isComplete,
    answersLength: answers.length,
    formStartTime,
    totalVisibleQuestions: visibleQuestions.length,
  });

  // Auto-scroll hooks
  useAutoScroll({
    currentQuestionIndex,
    visibleQuestions,
    answers,
    currentQuestionRef,
    messagesEndRef,
  });

  // Helper to set or overwrite answer by question ID
  const setAnswerById = useCallback((questionId, value) => {
    setAnswers((prev) => {
      const idx = questions.findIndex((q) => q.id === questionId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }, []);

  // Show summary screen when all questions are answered
  useEffect(() => {
    if (isComplete && answers.length > 0 && !showSummary) {
      setShowSummary(true);
    }
  }, [isComplete, answers.length, showSummary]);

  // Show first question after welcome messages complete
  const shouldShowFirstQuestion = welcomeMessageIndex >= 2;

  // Welcome message streaming effect
  useEffect(() => {
    if (welcomeMessageIndex < 2) {
      const timer = setTimeout(() => {
        setWelcomeMessageIndex(prev => prev + 1);
      }, 1000); // 1 second delay between messages
      return () => clearTimeout(timer);
    }
  }, [welcomeMessageIndex]);

  // Scroll to first question when it appears
  useEffect(() => {
    if (shouldShowFirstQuestion && currentQuestionRef.current) {
      // Longer delay to ensure smooth transition and avoid conflicts
      setTimeout(() => {
        currentQuestionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 500);
    }
  }, [shouldShowFirstQuestion]);


  // Auto-update when name, email, phone, DOB are present
  useEffect(() => {
    if (submitted || infantBlocked) return;
    if (hasPostedProgressRef.current || isUpdatingRef.current) return;

    const personal = getAnswerById(answers, questions, "personal-info");
    const contact = getAnswerById(answers, questions, "contact-info");

    const hasName = Boolean(personal?.name && personal.name.trim());
    const hasDob = Boolean(personal?.dateOfBirth);
    const hasEmail = Boolean(contact?.email && contact.email.trim());
    const hasPhone = Boolean(contact?.phoneNumber && contact.phoneNumber.trim());

    if (!hasName || !hasDob || !hasEmail || !hasPhone) return;

    // All required pieces exist — fire progress update
    isUpdatingRef.current = true;
    const payload = buildApiPayload(answers, questions);

    callBackendApiPost("/api/v1/update", payload)
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (data?.case?.id) {
          setAnswerById("case-id", data.case.id);
        }
        if (data?.patient?.id) {
          setAnswerById("patient-id", data.patient.id);
        }
        hasPostedProgressRef.current = true;
      })
      .catch((err) => {
        logDrtEvent(`[conv] Update api call failed for patient with contact: ${contact?.phoneNumber} with err: ${err}`);
      })
      .finally(() => {
        isUpdatingRef.current = false;
      });
  }, [answers, submitted, infantBlocked, setAnswerById]);

  const handleSubmitForm = useCallback(async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      // Track form completion
      const completionTime = Math.round((Date.now() - formStartTime) / 1000);
      trackFormCompleted(answers.length, completionTime);

      const res = await callBackendApiPost("/api/v1/submit", buildApiPayload(answers, questions));
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Submit failed (${res.status}) ${text}`);
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || "Submission failed");
      const patientID = getAnswerById(answers, questions, "patient-id");
      const caseID = getAnswerById(answers, questions, "case-id");
      logDrtEvent(`[conv] Submit api call failed for patient with patientID: ${patientID} and caseID: ${caseID} with err: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [answers, formStartTime]);

  const handleAnswer = (answer) => {
    const qIdx = questions.findIndex((qq) => qq.id === currentQuestion.id);
    const newAnswers = [...answers];
    newAnswers[qIdx] = answer;
    setAnswers(newAnswers);

    const currentQuestionDef = questions[qIdx];
    if (currentQuestionDef) {
      trackQuestionAnswered(
        currentQuestionDef.id,
        currentQuestionDef.text,
        answer,
        newAnswers.filter((a) => a != null).length,
        visibleQuestions.length
      );
    }

    const newVisible = getVisibleQuestions(questions, newAnswers);
    const nextQuestionIndex = currentQuestionIndex + 1;

    if (nextQuestionIndex < newVisible.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
    } else {
      // find next unanswered among *visible* by checking mapped indices
      let nextUnansweredIndex = newVisible.length;
      for (let i = 0; i < newVisible.length; i++) {
        const q = newVisible[i];
        const globalIdx = questions.findIndex((qq) => qq.id === q.id);
        if (!newAnswers[globalIdx]) {
          nextUnansweredIndex = i;
          break;
        }
      }
      setCurrentQuestionIndex(nextUnansweredIndex);
    }
  };

  const handleNext = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const handleEditQuestion = (questionId, newAnswer) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    if (questionIndex !== -1) {
      const newAnswers = [...answers];
      newAnswers[questionIndex] = newAnswer;
      setAnswers(newAnswers);

      // Track question edited event
      const question = questions[questionIndex];
      if (question) {
        trackQuestionEdited(questionId, question.text, newAnswer);
      }
    }
  };

  const handleBackToQuestions = () => {
    setShowSummary(false);
    // Find the last answered question to continue from
    let lastAnsweredIndex = -1;
    for (let i = 0; i < visibleQuestions.length; i++) {
      const question = visibleQuestions[i];
      const questionIndex = questions.findIndex((q) => q.id === question.id);
      if (answers[questionIndex]) {
        lastAnsweredIndex = i;
      }
    }
    setCurrentQuestionIndex(lastAnsweredIndex + 1);
  };

  const handleStartOverClick = () => {
    setShowStartOverModal(true);
  };

  const confirmStartOver = () => {
    setShowStartOverModal(false);
    setIsResetting(true);

    // Fade out animation
    setTimeout(() => {
      // Reset all state
      setCurrentQuestionIndex(0);
      setAnswers([]);
      setShowSummary(false);
      setSubmitting(false);
      setSubmitError("");
      setSubmitted(false);
      setInfantBlocked(false);
      setWelcomeMessageIndex(0); // Reset welcome message animation

      // Reset refs
      hasPostedProgressRef.current = false;
      isUpdatingRef.current = false;

      // Fade in animation
      setTimeout(() => {
        setIsResetting(false);
      }, 100);
    }, 300);
  };

  const cancelStartOver = () => {
    setShowStartOverModal(false);
  };

  const currentQuestion = visibleQuestions[currentQuestionIndex];

  const answeredCount = visibleQuestions.reduce((acc, vq) => {
    const idx = questions.findIndex((qq) => qq.id === vq.id);
    return acc + (answers[idx] != null ? 1 : 0);
  }, 0);

  const scrollToBottom = (behavior = "auto") => {
    // Save current scroll position to check if we actually need to scroll
    const currentScroll = window.scrollY;

    if (inputEndRef.current) {
      const rect = inputEndRef.current.getBoundingClientRect();
      const isAlreadyVisible = rect.bottom <= window.innerHeight;

      // Only scroll if the element is not fully visible
      if (!isAlreadyVisible) {
        inputEndRef.current.scrollIntoView({ behavior, block: "end" });
        // Add extra padding at the bottom to ensure Continue button is fully visible
        setTimeout(() => {
          window.scrollBy({ top: 100, behavior: behavior === "auto" ? "auto" : "smooth" });
        }, behavior === "smooth" ? 100 : 0);
      }
      return;
    }
    if (inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect();
      const isAlreadyVisible = rect.bottom <= window.innerHeight;

      if (!isAlreadyVisible) {
        inputWrapperRef.current.scrollIntoView({ behavior, block: "end" });
        setTimeout(() => {
          window.scrollBy({ top: 100, behavior: behavior === "auto" ? "auto" : "smooth" });
        }, behavior === "smooth" ? 100 : 0);
      }
      return;
    }
    if (messagesEndRef.current) {
      const rect = messagesEndRef.current.getBoundingClientRect();
      const isAlreadyVisible = rect.bottom <= window.innerHeight;

      if (!isAlreadyVisible) {
        messagesEndRef.current.scrollIntoView({ behavior, block: "end" });
        setTimeout(() => {
          window.scrollBy({ top: 100, behavior: behavior === "auto" ? "auto" : "smooth" });
        }, behavior === "smooth" ? 100 : 0);
      }
      return;
    }
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior });
  };

  // Track if we've already scrolled for the current question
  const hasScrolledForQuestion = useRef(null);

  // Scroll to next question after answering
  useEffect(() => {
    if (!currentQuestion) return;

    const isLong =
      currentQuestion.type === "tried" || currentQuestion.type === "prescriptions";

    // Normal (non-long) questions: ensure Continue button is always visible
    if (!isLong) {
      if (hasScrolledForQuestion.current !== currentQuestion.id && currentQuestionRef.current) {
        hasScrolledForQuestion.current = currentQuestion.id;

        const isTextInput = currentQuestion.type === 'text' ||
          currentQuestion.type === 'textarea' ||
          currentQuestion.id === 'contact-info' ||
          currentQuestion.id === 'personal-info';

        if (isTextInput) {
          // For text input questions with delayed focus (300ms), scroll AFTER keyboard appears
          if (currentQuestion.type === 'text' || currentQuestion.id === 'contact-info') {
            // Timing: 300ms (focus delay) + 400ms (keyboard animation) = 700ms total
            setTimeout(() => {
              // Scroll input wrapper (which contains the Continue button) into view
              inputWrapperRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
              });
            }, 700); // Scroll after keyboard is fully visible
            return;
          }

          // Special handling for personal info transition
          const isPersonalInfo = currentQuestion.id === 'personal-info';
          const delay = isPersonalInfo ? 1200 : 800; // Extra delay for personal info transition

          // For text inputs, scroll to ensure input and Continue button are visible
          setTimeout(() => {
            inputWrapperRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });
          }, delay);
        } else {
          // For chip selections and option buttons (sex, treatment, state), scroll input wrapper
          // This ensures both the question AND the Continue button are visible
          setTimeout(() => {
            inputWrapperRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });
          }, 600); // Longer delay for smoother transition
        }
      }
      return;
    }

    // Tall questions (tried/prescriptions): pin to bottom of input
    hasScrolledForQuestion.current = currentQuestion.id;

    // Don't use ResizeObserver for prescriptions - it causes jumpiness with progressive form fields
    // The Continue button has onFocus handler to scroll into view when needed
    const shouldObserveResize = currentQuestion.id !== 'prescriptions';
    const shouldInitialScroll = currentQuestion.id !== 'prescriptions';

    const t1 = shouldInitialScroll ? setTimeout(() => scrollToBottom("auto"), 50) : null;
    const t2 = shouldInitialScroll ? setTimeout(() => scrollToBottom("auto"), 250) : null;

    // Follow dynamic growth (buttons appearing, cards added, etc.)
    // Debounce scroll calls to prevent jumpiness during typing
    let resizeTimeout;
    let lastHeight = inputWrapperRef.current?.offsetHeight || 0;

    const ro = (inputWrapperRef.current && shouldObserveResize) ? new ResizeObserver((entries) => {
      clearTimeout(resizeTimeout);

      // Only scroll if the height increased significantly (more than 50px)
      // This prevents scrolling on minor text input changes
      const currentHeight = entries[0]?.target?.offsetHeight || 0;
      const heightDelta = currentHeight - lastHeight;

      if (heightDelta > 50) {
        // Debounce by 300ms to avoid rapid scroll calls
        resizeTimeout = setTimeout(() => {
          scrollToBottom("auto");
          lastHeight = currentHeight;
        }, 300);
      } else {
        lastHeight = currentHeight;
      }
    }) : null;

    ro?.observe(inputWrapperRef.current);

    return () => {
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
      clearTimeout(resizeTimeout);
      ro?.disconnect();
    };
  }, [currentQuestion]);

  // Show thank you screen if intake is submitted
  if (submitted) {
    return <ThankYouScreen answers={answers} />;
  }

  // Show summary screen if all questions are completed
  if (showSummary && !infantBlocked) {
    return (
      <SummaryScreen
        answers={answers}
        visibleQuestions={summaryQuestions}
        onEditQuestion={handleEditQuestion}
        onSubmitIntake={handleSubmitForm}
        onBackToQuestions={handleBackToQuestions}
        submitting={submitting}
        submitError={submitError}
        submitted={submitted}
      />
    );
  }

  return (
    <>
      <div className={`intake-container ${isResetting ? 'fade-out' : 'fade-in'}`}>
        {/* Header with integrated progress bar */}
        <div className="intake-header">
          <div className="intake-header-content">
            <img src="/PH.png" alt="Piction Health" className="intake-title" />
            <button
              className="start-over-button"
              onClick={handleStartOverClick}
              disabled={answeredCount === 0}
              title="Clear all answers and restart."
            >
              Start Over
            </button>
          </div>

          {/* Progress Bar - Thin line at bottom */}
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(answeredCount / visibleQuestions.length) * 100}%` }}
            />
          </div>
        </div>

      {/* Chat Messages */}
      <div className="intake-messages" ref={messagesContainerRef}>
        {/* Welcome messages with streaming animation */}
        {welcomeMessageIndex >= 0 && (
          <div className="message-container">
            <div className="message-bubble assistant-message">
              <p>
                👋 Hello! I'm here to help you get started with your care journey. Let me ask you a few questions to better
                understand your needs.
              </p>
            </div>
          </div>
        )}
        {welcomeMessageIndex >= 1 && (
          <div className="message-container">
            <div className="message-bubble assistant-message">
              <p>
                📋 You will be able to review and edit your answers before submitting this intake form. You can also reset the form at any time by clicking the <strong>Start Over</strong> button at the top of the page.
              </p>
            </div>
          </div>
        )}

          {/* Q&A Messages */}
          {visibleQuestions.map((q) => {
            const idx = questions.findIndex((qq) => qq.id === q.id);
            const answer = answers[idx];

            // Allow falsy values like '', 0, false. Only skip null/undefined.
            if (answer == null) return null;

            const rendered = formatAnswer(answer, q.type);

            // Skip rendering the pair if there's nothing to show (e.g., credit-card)
            if (rendered == null) return null;

            return (
              <div key={q.id} className="message-group">
                <div className="message-container">
                  <div className="message-bubble assistant-message">
                    <p>{q.text}</p>
                  </div>
                </div>
                <div className="message-container user-message">
                  <div className="message-bubble user-message-bubble">{rendered}</div>
                </div>
              </div>
            );
          })}

          {/* Credit Card Success Message */}
          {(() => {
            const creditCardIdx = questions.findIndex((qq) => qq.id === 'credit-card');
            if (creditCardIdx === -1) return null;
            
            const creditCardAnswer = answers[creditCardIdx];
            const isSuccess = creditCardAnswer && (
              creditCardAnswer.status === 'succeeded' || 
              creditCardAnswer.status === 'requires_capture'
            );
            
            if (!isSuccess) return null;
            
            // Check if credit card question is in visible questions and we've answered it
            const creditCardVisibleIdx = visibleQuestions.findIndex((q) => q.id === 'credit-card');
            if (creditCardVisibleIdx === -1) return null;
            
            // Show success message if we've moved past the credit card question
            const hasAnsweredCreditCard = currentQuestionIndex > creditCardVisibleIdx;
            if (!hasAnsweredCreditCard) return null;
            
            const amount = creditCardAnswer.amount ? (creditCardAnswer.amount / 100).toFixed(2) : '0.00';
            const currency = creditCardAnswer.currency?.toUpperCase() || 'USD';
            const brand = creditCardAnswer.brand 
              ? creditCardAnswer.brand.charAt(0).toUpperCase() + creditCardAnswer.brand.slice(1) 
              : 'Card';
            const last4 = creditCardAnswer.last4 || '****';
            const paymentType = creditCardAnswer.mode === 'self-pay' ? 'Payment' : 'Authorization';
            const paymentText = creditCardAnswer.mode === 'self-pay' 
              ? `Your payment of $${amount} ${currency} has been successfully processed.`
              : `Your card ending in ${last4} has been authorized for $${amount} ${currency}.`;
            
            return (
              <div key="credit-card-success" className="message-container">
                <div className="message-bubble assistant-message instruction-message">
                  <p>
                    <strong>✅ {paymentType} Successful</strong>
                    <br />
                    {paymentText}
                    <br />
                    <span style={{ fontSize: '0.85em', opacity: 0.8 }}>
                      {brand} •••• {last4}
                    </span>
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Current Question (hide if infant blocked) */}
          {currentQuestion && !infantBlocked && shouldShowFirstQuestion && (
            <div className="message-container first-question-container" ref={currentQuestionRef}>
              <div className="message-bubble assistant-message">
                <p>{currentQuestion.text}</p>
                {currentQuestion.id === 'contact-info' && (
                  <p className="sms-consent-text">
                    By entering your phone number, you agree to receive SMS messages from Piction Health. Message and data rates may apply.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Completion message (pre-submit) */}
          {isComplete && !submitted && !infantBlocked && (
            <div className="message-container">
              <div className="message-bubble assistant-message">
                <p>
                  You're almost done — please review and press <strong>Submit</strong> below.
                </p>
              </div>
            </div>
          )}

          {/* Infant age-block message */}
          {infantBlocked && (
            <>
              <div className="message-container">
                <div className="message-bubble assistant-message">
                  <p>
                    We're sorry, we unfortunately are not able to see patients under age 2. Please
                    call 911 in case of an emergency or consult a pediatrician.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Success bubble after submit */}
          {submitted && (
            <div className="message-container">
              <div className="message-bubble assistant-message">
                <p>Thank you for completing the intake! We've received your information.</p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

      {/* Input Form (hidden when infantBlocked) */}
      {currentQuestion && !infantBlocked && shouldShowFirstQuestion && (
        <div className="intake-input-container first-input-container">
          <div className="input-wrapper" ref={inputWrapperRef}>
            {renderQuestionComponent({
              currentQuestion,
              questions,
              answers,
              onSubmit: handleAnswer,
              onNext: handleNext,
              setInfantBlocked,
              setCurrentQuestionIndex,
              visibleQuestions,
              ensureFullyVisible: () => scrollToBottom("smooth"),
            })}
            <p className="input-hint">{currentQuestion.hint}</p>

            <div ref={inputEndRef} style={{ height: 1 }} />
          </div>
        </div>
      )}

        {/* Submit Footer (hidden when infantBlocked) */}
        {!currentQuestion && answers.length > 0 && !infantBlocked && (
          <div className="intake-submit-container">
            <button className="submit-button" onClick={handleSubmitForm}>
              {submitting ? "Submitting…" : "Submit"}
            </button>

            {submitError && <p className="submit-error">Error: {submitError}</p>}
            {submitted && <p className="submit-success">Thanks! Your intake was submitted.</p>}
          </div>
        )}
      </div>

      {/* Start Over Confirmation Modal - Rendered via Portal */}
      {showStartOverModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Start Over?</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to start over? This will clear all your answers and you'll need to begin from the first question.</p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-button modal-button-secondary"
                onClick={cancelStartOver}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button-primary"
                onClick={confirmStartOver}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default Intake;
